import { IPlanGroup, IPlanExercise } from '../../types/workout/plan-state';

/**
 * Columns that the plan editor table renders for every block.
 * Always shown regardless of content.
 */
export const BASE_COLUMNS = ['sets', 'repetitions', 'weight', 'restInterval'] as const;
export type BaseColumn = (typeof BASE_COLUMNS)[number];

/**
 * Columns that only appear when at least one exercise (or its setConfiguration)
 * in the block has a non-empty value for them. Design handoff §4.4.
 */
export const OPTIONAL_COLUMNS = ['tempo', 'rpe', 'distance', 'time', 'difficulty'] as const;
export type OptionalColumn = (typeof OPTIONAL_COLUMNS)[number];

export type AdaptiveColumn = BaseColumn | OptionalColumn;

/**
 * Compute which optional columns are "in use" within [group].
 *
 * An optional column is considered in use if any exercise in the group, or any
 * of its `setConfiguration` entries, has a non-empty value for that field.
 *
 * Returns the optional columns in their canonical order. Combine with
 * `BASE_COLUMNS` for the full render list.
 */
export function computeUsedOptionalColumns(group: IPlanGroup): OptionalColumn[] {
  const used = new Set<OptionalColumn>();
  for (const exercise of group.exercises) {
    const sources: Record<string, unknown>[] = [exercise as unknown as Record<string, unknown>];
    if (Array.isArray((exercise as IPlanExercise).setConfiguration)) {
      for (const cfg of (exercise as IPlanExercise).setConfiguration ?? []) {
        sources.push(cfg as unknown as Record<string, unknown>);
      }
    }
    for (const col of OPTIONAL_COLUMNS) {
      const found = sources.some((src) => {
        const v = src[col];
        return v !== undefined && v !== null && String(v).trim().length > 0;
      });
      if (found) used.add(col);
    }
  }
  return OPTIONAL_COLUMNS.filter((c) => used.has(c));
}

/** Returns the full ordered column list (base + used optional). */
export function computeAdaptiveColumns(group: IPlanGroup): AdaptiveColumn[] {
  return [...BASE_COLUMNS, ...computeUsedOptionalColumns(group)];
}
